import { useCallback, useEffect, useState } from "react";
import { useServerPagination } from "../../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../../hooks/customHook/paginationHook/usePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Col,
  Container,
  FormControl,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { ProductDeficitModal } from "./ProductDeficitModal";
import BASE_URL from "../../../../../assets/global/url";
import axios from "axios";
import {
  formatToNumber,
  formatToTwoDecimal,
} from "../../../../../utils/numberFormatter";

const DEFICIT_PRODUCT_URL = `${BASE_URL}/inventoryReport/deficitProduct`;
const DEFICIT_TOTALS_URL = `${BASE_URL}/inventoryReport/deficitProduct/totals`;
const DEBOUNCE_MS = 500;

const PRODUCT_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "item_code", label: "Item Code" },
  { value: "product_name", label: "Product Name" },
  { value: "unit_of_measurement", label: "Unit of Measurement" },
];

const ProductDeficitTab = ({ productCategory, thisFromdate, thisTodate }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totals, setTotals] = useState(null);
  const [productSearchText, setProductSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [productFilterColumn, setProductFilterColumn] = useState("all");

  const pagination = useServerPagination(DEFICIT_PRODUCT_URL, 10, {
    startDate: thisFromdate,
    endDate: thisTodate,
    productCategory,
    searchText: "",
    filterColumn: "all",
  });

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(productSearchText.trim()),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [productSearchText]);

  const fetchTotals = useCallback((params) => {
    axios
      .get(DEFICIT_TOTALS_URL, { params })
      .then((res) => setTotals(res.data))
      .catch((err) =>
        console.error("[ProductDeficitTab] Error fetching totals:", err),
      );
  }, []);

  useEffect(() => {
    if (!thisFromdate || !thisTodate || !productCategory) return;

    const params = {
      startDate: thisFromdate,
      endDate: thisTodate,
      productCategory,
      searchText: debouncedSearch,
      filterColumn: productFilterColumn,
    };

    pagination.updateParams(params);
    fetchTotals(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    thisFromdate,
    thisTodate,
    productCategory,
    debouncedSearch,
    productFilterColumn,
  ]);

  const handleOpenModal = (productObj) => {
    setSelectedProduct(productObj);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const hasData = pagination.data && pagination.data.length > 0;
  const showFooter = !pagination.loading && totals && hasData;
  const activeFilterLabel =
    PRODUCT_FILTER_OPTIONS.find((o) => o.value === productFilterColumn)
      ?.label ?? "All";

  return (
    <Container fluid>
      <Row>
        <Col sm className="mb-3">
          <div className="w-100 d-flex align-items-center mt-3 mb-3">
            <h5>{productCategory} Deficit Stocks</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>

          <div className="mt-3 mb-4">
            <InputGroup className="mb-3">
              <FormControl
                type="text"
                placeholder={`Search by ${activeFilterLabel}…`}
                value={productSearchText}
                onChange={(e) => setProductSearchText(e.target.value)}
              />

              <Button
                type="button"
                variant="outline-secondary"
                className="dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa-solid fa-sliders" />
              </Button>

              <ul className="dropdown-menu dropdown-menu-end">
                {PRODUCT_FILTER_OPTIONS.map(({ value, label }) => (
                  <li key={value}>
                    <button
                      className={`dropdown-item ${productFilterColumn === value ? "active" : ""}`}
                      onClick={() => setProductFilterColumn(value)}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </InputGroup>
          </div>

          <Table bordered responsive className="text-nowrap">
            <thead className="thead-light align-middle text-center">
              <tr>
                <th rowSpan="2" style={{ minWidth: "150px" }}>
                  Item Code
                </th>
                <th rowSpan="2" style={{ minWidth: "250px" }}>
                  Product Name
                </th>
                <th rowSpan="2" style={{ minWidth: "60px" }}>
                  Unit of Measurement
                </th>
                <th colSpan="2">Sales Invoice</th>
                <th colSpan="2">Production</th>
                <th rowSpan="2" style={{ minWidth: "200px" }}>
                  Overall Deficit Stocks
                </th>
                <th rowSpan="2" style={{ minWidth: "100px" }}>
                  Action
                </th>
              </tr>
              <tr>
                <th style={{ minWidth: "180px" }}>Transactions</th>
                <th style={{ minWidth: "180px" }}>Deficit Stocks</th>
                <th style={{ minWidth: "180px" }}>Transactions</th>
                <th style={{ minWidth: "180px" }}>Deficit Stocks</th>
              </tr>
            </thead>

            <tbody className="align-middle">
              {pagination.loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />{" "}
                    Fetching Deficit Data...
                  </td>
                </tr>
              ) : hasData ? (
                pagination.data.map((item, idx) => (
                  <tr
                    key={item.product_id ?? idx}
                    onClick={() => handleOpenModal(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="text-center fw-bold">{item.product_code}</td>
                    <td>{item.product_name}</td>
                    <td>{item.unit_of_measure}</td>

                    {/* Sales Invoice */}
                    <td className="text-center">
                      {formatToNumber(item.si_count)}
                    </td>
                    <td className="text-center text-danger fw-semibold">
                      {formatToNumber(item.si_deficit)}
                    </td>

                    {/* Production */}
                    <td className="text-center">
                      {formatToNumber(item.prod_count)}
                    </td>
                    <td className="text-center text-danger fw-semibold">
                      {formatToNumber(item.prod_deficit)}
                    </td>

                    {/* Overall */}
                    <td className="text-center text-danger fw-bold fs-6">
                      {formatToNumber(item.overall_deficit)}
                    </td>

                    <td className="text-center">
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(item);
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    <em>No deficit records found for the selected period.</em>
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="font-weight-bold table-primary">
              {showFooter && (
                <tr>
                  <td colSpan="3" className="text-center fw-bold align-middle">
                    Total:
                  </td>
                  <td className="text-center fw-bold align-middle">
                    {formatToNumber(totals.total_si_count)}
                  </td>
                  <td className="text-center text-danger fw-bold align-middle">
                    {formatToNumber(totals.total_si_deficit)}
                  </td>
                  <td className="text-center fw-bold align-middle">
                    {formatToNumber(totals.total_prod_count)}
                  </td>
                  <td className="text-center text-danger fw-bold align-middle">
                    {formatToNumber(totals.total_prod_deficit)}
                  </td>
                  <td className="text-center text-danger fw-bold fs-5 align-middle">
                    {formatToNumber(totals.total_overall_deficit)}
                  </td>
                  <td />
                </tr>
              )}
            </tfoot>
          </Table>

          <PaginationControls {...pagination} />
        </Col>
      </Row>

      <ProductDeficitModal
        show={showModal}
        onHide={handleCloseModal}
        selectedProduct={selectedProduct}
        thisFromdate={thisFromdate}
        thisTodate={thisTodate}
      />
    </Container>
  );
};

export { ProductDeficitTab };
